import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { valueRender } from '@repo/toolkit/conditional-render'
import { DateFormatter } from '@repo/toolkit/date'
import { toFullName } from '@repo/toolkit/to-full-name'
import { useNavigator } from '@repo/toolkit/use-navigator'
import Badge from '@repo/ui/badge'
import { Column } from '@repo/ui/data-table'
import {
  BuildingThreeIcon,
  CalendarSevenIcon,
  GoalIcon,
  MeetingPointIcon,
  PlusIcon,
  UploadIcon
} from '@repo/ui/icons'
import Modal from '@repo/ui/modal'
import RoundedIcon from '@repo/ui/rounded-icon'
import Typography from '@repo/ui/typography'
import React, { useState } from 'react'

import { API } from '../../api/apis.config'
import CountryFlag from '../../components/elements/country-flag'
import FilterDrawer from '../../components/elements/drawers/filter-drawer'
import DynamicDataTable, { DynamicTableRef } from '../../components/elements/dynamic-table'
import { countFilters } from '../../components/elements/dynamic-table/utils/countFilters'
import UserNavBar from '../../components/elements/nav-bar'
import TransportPicto from '../../components/elements/transport-picto'
import { TripBookingItem } from '../../components/pages/trip-bookings/trip-booking.ui'
import TripBookingLock from '../../components/pages/trip-bookings/trip-booking-lock'
import TripBookingsFilter from '../../components/pages/trip-bookings/trip-bookings-filter'
import { TripBookingAllocations } from '../../components/pages/trip-bookings/tripbooking-allocation'
import { NumberOfPlacesBadge } from '../../components/pages/trip-dates/trip-dates.ui'
import { Paths } from '../../lib/constants/path'
import { useDownloadDocuments } from '../../lib/hooks/use-download-documents'
import { useFilters } from '../../lib/hooks/use-filters'
import { useModalManager } from '../../lib/hooks/use-modal-manager'
import { transportTypeMapping, tripTypeMapping } from '../../lib/types/enums/trip/trip-type.enum'
import { TripBookingProperties } from '../../lib/types/models/trip-booking/properties.trip-booking'
import {
  TripBookingTypes,
  TripBookingVersion
} from '../../lib/types/models/trip-booking/trip-booking'
import {
  transformTripBookingFiltersToUrl,
  transformUrlToTripBookingFilters
} from '../../lib/utils/filters-mapping/trip-booking'
import { getTranslatedName } from '../../lib/utils/functions/translation.functions'

const TripBookingListPage = () => {
  const { navigate, navigateWithEvent } = useNavigator()
  const { downloadDocuments } = useDownloadDocuments()
  const tableRef = React.useRef<DynamicTableRef<TripBookingTypes.Full>>(null)
  const {
    modalState,
    toggleModal,
    closeModel,
    selectedData: selectedTripBookingId
  } = useModalManager<'filters' | 'centers', number>(['filters', 'centers'])
  const [isTableLoading, setIsTableLoading] = useState(false)
  const [selectedData, setSelectedData] = useState<TripBookingTypes.Compact[]>([])

  const { selectedFilter, filter, onFilterChange, resetFilters, onSelectedFilterChange } =
    useFilters<TripBookingTypes.Filters>({
      initialFilters: {},
      options: { allowSearchParam: true },
      transformFilterToUrl: transformTripBookingFiltersToUrl,
      transformUrlToFilter: transformUrlToTripBookingFilters
    })

  const columns: Column<TripBookingTypes.Full>[] = [
    {
      accessorKey: 'locked',
      header: 'Verrouillé',
      enableSorting: true,
      cell: ({ row }) => (
        <TripBookingLock tripBookingId={row.original.id} tripBookingLocked={row.original.locked} />
      )
    },
    {
      accessorKey: 'lecResponsable.firstName',
      header: 'Resp résa',
      enableSorting: true,
      cell: ({ row }) => {
        const tripBooking = row.original
        if (tripBooking.isTakeInCharge || !tripBooking.lecResponsable) return '-'
        return toFullName(
          tripBooking.lecResponsable.firstName,
          tripBooking.lecResponsable.lastName
        )
      }
    },
    {
      accessorKey: 'relatedTrip.type',
      header: 'Type de voyage',
      enableSorting: true,
      cell: ({ row: { original } }) =>
        original.relatedTrip?.type ? (
          <Badge
            text={tripTypeMapping[original.relatedTrip.type].label}
            variant={tripTypeMapping[original.relatedTrip.type].variant}
          />
        ) : (
          '-'
        )
    },
    {
      accessorKey: 'reservationNumber',
      header: '# de réservation',
      enableSorting: true,
      cell: ({ row }) => valueRender(row.original.reservationNumber)
    },
    {
      accessorKey: 'directionality',
      header: 'Type de trajet',
      enableSorting: true,
      cell: ({ row: { original } }) => (
        <Badge
          text={transportTypeMapping[original.directionality].label}
          variant={transportTypeMapping[original.directionality].variant}
        />
      )
    },
    {
      accessorKey: 'tripSteps',
      header: 'Dates du trajet',
      enableSorting: false,
      cell: ({ row: { original } }) => {
        if (original.isTakeInCharge)
          return <Badge text={DateFormatter.DDMMYYYY(original.date)} variant="grey" />
        const dates = TripBookingProperties.getTripStepDates(original)
        if (dates.length === 0) return '-'
        return (
          <Badge
            text={dates.map((date) => DateFormatter.DDMMYYYY(date)).join(' - ')}
            variant="grey"
          />
        )
      }
    },
    {
      accessorKey: 'transportMeans.name',
      header: 'Transport',
      enableSorting: true,
      cell: ({ row }) => {
        const tripBooking = row.original
        if (tripBooking.isTakeInCharge)
          return (
            <TransportPicto.Container className="bg-vividBlue-light">
              <MeetingPointIcon className="w-6 h-6" color={tailwindColors.vividBlue.DEFAULT} />
            </TransportPicto.Container>
          )
        return tripBooking.transportMeans?.picto ? (
          <TransportPicto picto={tripBooking.transportMeans?.picto} />
        ) : (
          getTranslatedName(tripBooking.transportMeans) || '-'
        )
      }
    },
    {
      accessorKey: 'tripProvider.companyName',
      header: 'Prestataire',
      enableSorting: true,
      cell: ({ row }) => {
        const tripBooking = row.original
        if (tripBooking.isTakeInCharge) return '-'
        return tripBooking.tripProvider?.companyName ?? '-'
      }
    },
    {
      accessorKey: 'travelCompany',
      header: 'Compagnie',
      enableSorting: false,
      cell: ({ row: { original } }) => original.travelCompany?.name ?? '-'
    },
    {
      accessorKey: 'departurePlace.country',
      header: 'Pays de départ',
      cell: ({ row }) => (
        <CountryFlag
          possibleCountry={
            TripBookingProperties.getTripStepDetails(row.original).departurePlace?.country
          }
        />
      )
    },
    {
      accessorKey: 'departurePlace.city',
      header: 'Ville de départ',
      enableSorting: true,
      cell: ({ row: { original } }) =>
        TripBookingProperties.getTripStepDetails(original)?.departurePlace?.city ?? '-'
    },
    {
      accessorKey: 'arrivalPlace.country',
      header: `Pays d'arrivée`,
      cell: ({ row }) => (
        <CountryFlag
          possibleCountry={
            TripBookingProperties.getTripStepDetails(row.original)?.arrivalPlace?.country
          }
        />
      )
    },
    {
      accessorKey: 'arrivalPlace.city',
      header: `Ville d'arrivée`,
      enableSorting: true,
      cell: ({ row: { original } }) =>
        TripBookingProperties.getTripStepDetails(original)?.arrivalPlace?.city ?? '-'
    },
    {
      accessorKey: 'numberOfStaffPlacesReserved',
      header: '# Places réservées',
      centerCell: true,
      cell: ({ row }) => (
        <NumberOfPlacesBadge
          className={'py-2'}
          studentsPlaces={row.original.numberOfStudentsPlacesReserved}
          staffPlaces={row.original.numberOfStaffPlacesReserved}
        />
      )
    },
    {
      accessorKey: 'numberOfStaffPlacesAllocated',
      header: '# Places réparties',
      centerCell: true,
      cell: ({ row }) => (
        <NumberOfPlacesBadge
          className={'py-2'}
          studentsPlaces={row.original.numberOfStudentsPlacesAllocated}
          staffPlaces={row.original.numberOfStaffPlacesAllocated}
        />
      )
    },
    {
      accessorKey: 'numberOfStaffPlacesDispatched',
      header: '# Places dispatchées',
      centerCell: true,
      cell: ({ row }) => (
        <NumberOfPlacesBadge
          className={'py-2'}
          studentsPlaces={row.original.numberOfStudentsPlacesDispatched}
          staffPlaces={row.original.numberOfStaffPlacesDispatched}
        />
      )
    },
    {
      accessorKey: 'id',
      header: 'Réservation liée',
      centerCell: true,
      cell: ({ row }) => {
        const tripBooking = row.original

        if (!tripBooking.mainReservationId && !tripBooking.secondaryReservation) return '-'
        return (
          <div className="bg-white-hover px-3 py-2 rounded border border-grey-light flex-wrap flex gap-2 w-full min-w-56 max-w-56 my-2">
            {tripBooking.mainReservation && (
              <TripBookingItem
                type={'main'}
                tripBooking={tripBooking.mainReservation}
                options={{ displayLabel: true }}
              />
            )}

            {tripBooking.secondaryReservation && (
              <TripBookingItem
                type={'secondary'}
                tripBooking={tripBooking}
                options={{ displayLabel: true }}
              />
            )}
          </div>
        )
      }
    },
    {
      accessorKey: 'paxListExportedAt',
      header: 'Liste Pax exportée le',
      enableSorting: true,
      centerCell: true,
      cell: ({ row }) => {
        const paxListExportedAt = row.original.paxListExportedAt
        if (!paxListExportedAt) return '-'
        return <Badge text={DateFormatter.DDMMYYYY(paxListExportedAt)} />
      }
    },
    {
      accessorKey: 'missions',
      header: 'Commande de missions',
      enableSorting: false,
      cell: ({ row }) => (
        <RoundedIcon
          variant={!row.original?.missions?.length ? 'danger' : 'success'}
          icon={GoalIcon}
          size={28}
          className="rounded-sm"
        />
      )
    },
    {
      accessorKey: 'centres',
      header: 'Centres',
      cell: ({ row }) => {
        const centers = row.original.centers
        if (!centers || centers.length === 0) return '-'
        return centers.map((center) => center.name).join(', ')
      }
    },
    {
      accessorKey: 'centres',
      header: '',
      cell: ({ row }) => {
        const centers = row.original.centers
        if (!centers || centers.length === 0) return '-'
        return (
          <Typography.Link
            variant="label-medium"
            color={tailwindColors.primary.DEFAULT}
            onClick={(e) => {
              e.stopPropagation()
              toggleModal('centers', row.original.id)
            }}
          >
            Voir les centres
          </Typography.Link>
        )
      }
    }
  ]

  return (
    <div>
      <UserNavBar title={'Gestion des Réservations'} icon={CalendarSevenIcon} />
      <div className={'h-4'} />

      <DynamicDataTable<TripBookingTypes.Full, TripBookingTypes.Filters, never, TripBookingVersion>
        ref={tableRef}
        title={'Toutes les réservations'}
        deleteMessage={{
          name: 'réservation',
          attributeName: 'id',
          deleteMany: 'Êtes-vous sûr de vouloir supprimer ces réservations ?'
        }}
        columns={columns}
        version="FULL"
        onLoadingChange={setIsTableLoading}
        selectable
        allowSearchParam
        onSelectedDataChange={({ rows }) => setSelectedData(rows)}
        onFiltersClick={() => toggleModal('filters')}
        filters={selectedFilter}
        filtersLength={countFilters(selectedFilter)}
        onRowClick={(tripBooking, event) =>
          navigateWithEvent(
            Paths.TRIP_BOOKING_UPDATE_PAGE.replace(':id', tripBooking.id.toString()),
            event
          )
        }
        endpoint={API['trip-bookings'].getAll.url}
        actions={[
          {
            type: 'global',
            icon: PlusIcon,
            refetch: true,
            variant: 'primary',
            label: 'Ajouter une nouvelle réservation',
            fn: () => navigate(Paths.TRIP_BOOKING_CREATE_PAGE)
          },
          {
            type: 'multiple',
            icon: UploadIcon,
            refetch: true,
            variant: 'ghost',
            label: 'Exporter',
            fn: (rows) => {
              const ids = rows.map((row) => row.id)
              downloadDocuments({ ids, endpoint: API['trip-bookings'].exportExcel.url })
            }
          },
          {
            type: 'multiple',
            icon: UploadIcon,
            disabled: selectedData.length === 0 || selectedData.some((row) => row.isTakeInCharge),
            variant: 'ghost',
            label: 'Export les listes de pax',
            fn: async (rows) => {
              await downloadDocuments({
                ids: rows.map((row) => row.id),
                endpoint: API['trip-bookings'].exportPassengersExcel.url
              })

              tableRef.current?.refetch()
            }
          }
        ]}
      />

      <FilterDrawer
        isOpen={modalState.filters}
        selectedFilter={selectedFilter}
        isLoading={isTableLoading}
        onReset={resetFilters}
        onFilterChange={onFilterChange}
        onClose={() => closeModel('filters')}
        onApply={() => onSelectedFilterChange({ ...filter })}
      >
        <TripBookingsFilter filter={filter} onFilterChange={onFilterChange} />
      </FilterDrawer>

      {selectedTripBookingId && (
        <Modal
          isOpen={modalState.centers}
          onCancel={() => closeModel('centers')}
          title="Voir les centres"
          icon={BuildingThreeIcon}
          hideActions
          size="medium"
        >
          <TripBookingAllocations
            tripBookingId={selectedTripBookingId}
            onCancel={() => closeModel('centers')}
          />
        </Modal>
      )}
    </div>
  )
}

export default TripBookingListPage
